import { Link } from "react-router-dom";
import { ArrowRight, Users, Zap, Scale, Lightbulb, TrendingUp, Globe, Linkedin, Flame } from "lucide-react";
import { FadeUp, SectionTag } from "../components/site/Reveal";
import { ROY_PHOTO, TIMELINE, LINKEDIN_URL, CORE_VALUES, IMPACT_GOALS } from "../content";

const VALUE_ICONS = [Users, Zap, Scale, Lightbulb, TrendingUp, Globe];

export default function About() {
  return (
    <main data-testid="about-page" className="pt-24">
      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
        <div className="grid items-start gap-16 lg:grid-cols-5">
          <FadeUp className="lg:col-span-2">
            <div className="relative" data-testid="about-portrait-frame">
              <div className="absolute -inset-3 border border-gold/60" />
              <div className="absolute -left-8 -top-8 h-40 w-40 rounded-full bg-gold/30 blur-3xl" />
              <img
                src={ROY_PHOTO}
                alt="Roy Buncome, Strategy Architect"
                className="relative aspect-[4/5] w-full object-cover object-top"
                data-testid="about-portrait-img"
              />
              <div className="relative mt-0 border border-gold/40 bg-white p-6">
                <p className="font-serif text-2xl font-semibold text-ink">Roy Buncome</p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.25em] text-golddeep">
                  Founder · Strategy Architect · CollabIgnite
                </p>
                <p className="mt-3 text-sm text-slate-500">Jacksonville, Florida · 25+ years · Financial Services</p>
                <a
                  data-testid="about-linkedin-btn"
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 text-sm text-golddeep transition-colors hover:text-gold"
                >
                  <Linkedin size={15} /> Verify me on LinkedIn
                </a>
              </div>
            </div>
          </FadeUp>

          <div className="lg:col-span-3">
            <FadeUp>
              <SectionTag>About Roy</SectionTag>
              <h1 className="mt-4 font-serif text-4xl font-bold tracking-tight text-ink sm:text-5xl">
                The steady hand with the <span className="gold-text italic">sharp edge.</span>
              </h1>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="mt-8 space-y-5 text-base leading-relaxed text-slate-600">
                <p>
                  You get a senior strategy and business development leader with 25+ years
                  accelerating growth across technology, government, defense, financial
                  services, and emerging markets. High-leverage revenue opportunities, new
                  markets, scalable business models, and partnerships that directly move
                  your P&amp;L.
                </p>
                <p>
                  People call him an architect because that is the actual job: putting
                  deals together. In seconds he recognizes who in a room can make a real
                  deal, and you get connected with them. Three of his clients' companies
                  have exited, from $30 million to $100 million.
                </p>
                <p className="border-l-2 border-gold pl-5 font-serif text-xl italic text-ink">
                  "I'm a corporate assassin. I help eliminate my client's competitors. And I'm
                  your capital raise's best wingman."
                </p>
                <p>
                  The edge is real, but so is the steadiness. Dependable. Immovable on your
                  interests. Patient when patience wins, decisive when the window opens.
                  You get both.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section data-testid="origin-section" className="border-t border-gold/25 bg-cream">
        <div className="mx-auto grid max-w-7xl items-start gap-14 px-6 py-24 lg:grid-cols-2 lg:px-10">
          <FadeUp>
            <SectionTag>The Origin</SectionTag>
            <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
              Why CollabIgnite exists
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600">
              Built in Jacksonville. Scaled globally. After decades working with
              entrepreneurs, technology, and business development, one pattern kept
              repeating: most businesses do not fail for lack of passion.
            </p>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-600">
              They fail for lack of strategy, relationships, systems, access,
              accountability, and capital pathways. CollabIgnite exists to bridge that
              gap for you: the right people, resources, and strategies, connected at the
              right time to ignite your growth.
            </p>
          </FadeUp>
          <FadeUp delay={0.12}>
            <div className="space-y-6">
              <div className="border border-gold/40 bg-white p-8">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-golddeep">Mission</p>
                <p className="mt-3 font-serif text-xl leading-relaxed text-ink">
                  To empower entrepreneurs and businesses with the clarity, relationships,
                  strategies, and resources needed to create sustainable growth and
                  economic impact.
                </p>
              </div>
              <div className="border border-gold/40 bg-white p-8">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-golddeep">Vision</p>
                <p className="mt-3 font-serif text-xl leading-relaxed text-ink">
                  To become the leading force in driving transformative growth through
                  collaboration, helping you build a company that creates wealth, jobs,
                  and community impact.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <section data-testid="impact-section" className="border-t border-gold/25">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
          <FadeUp>
            <SectionTag>The Impact Mission</SectionTag>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
              Where your company could be headed
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-px bg-gold/25 sm:grid-cols-3">
            {IMPACT_GOALS.map((g, i) => (
              <FadeUp key={g.value} delay={i * 0.08}>
                <div data-testid={`impact-goal-${i}`} className="flex h-full flex-col items-start bg-paper p-10">
                  <Flame size={20} className="text-golddeep" />
                  <p className="mt-6 font-serif text-6xl font-bold text-ink">{g.value}</p>
                  <p className="mt-2 text-sm uppercase tracking-wider text-slate-500">{g.label}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-timeline" className="border-t border-gold/25 bg-cream">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
          <FadeUp>
            <SectionTag>The Record</SectionTag>
            <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
              25+ years, chapter by chapter
            </h2>
          </FadeUp>
          <div className="mt-14 space-y-0">
            {TIMELINE.map((t, i) => (
              <FadeUp key={t.era} delay={i * 0.06}>
                <div
                  data-testid={`timeline-item-${i}`}
                  className="group grid gap-3 border-l border-gold/40 py-8 pl-8 transition-colors duration-500 hover:border-golddeep md:grid-cols-4 md:gap-8"
                >
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-golddeep md:pt-1.5">
                    {t.era}
                  </p>
                  <div className="md:col-span-3">
                    <h3 className="font-serif text-xl font-semibold text-ink">{t.title}</h3>
                    <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-slate-600">{t.body}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.15}>
            <div className="mt-8 border border-gold/30 bg-white p-6 text-sm text-slate-600">
              <span className="text-ink font-semibold">Also in the record:</span> Florida
              Leadership Trust, National Federation of Independent Business (2015 to 2024),
              advocating for small business owners in Washington and all 50 state capitals.
              Eight professional certifications. Eighteen public recommendations on LinkedIn,
              and counting.
            </div>
          </FadeUp>
        </div>
      </section>

      <section data-testid="about-values" className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
        <FadeUp>
          <SectionTag>Core Values</SectionTag>
          <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
            Six values that never move
          </h2>
        </FadeUp>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {CORE_VALUES.map((v, i) => {
            const Icon = VALUE_ICONS[i];
            return (
              <FadeUp key={v.title} delay={i * 0.06}>
                <article
                  data-testid={`value-card-${i}`}
                  className="h-full border border-gold/25 bg-white p-8 transition-colors duration-500 hover:border-golddeep/60"
                >
                  <Icon size={24} className="text-golddeep" />
                  <h3 className="mt-5 font-serif text-xl font-semibold text-ink">{v.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{v.body}</p>
                </article>
              </FadeUp>
            );
          })}
        </div>
        <FadeUp delay={0.2}>
          <div className="mt-16 text-center">
            <Link to="/book" data-testid="about-book-btn" className="btn-gold">
              Have a Conversation with Roy <ArrowRight size={16} />
            </Link>
          </div>
        </FadeUp>
      </section>
    </main>
  );
}
